/**
 * S17 — the add-column getter picker. A small popover listing the distinct
 * `@field` / `@@aspect` getters found across the table's shown rows, plus a
 * free-text entry for anything not enumerated (a getter on a not-yet-populated
 * field, a capability getter). Committing calls back with a parsed
 * {@link TableGetterSpec}; the container turns it into a `setTableColumns` add.
 */
import { useState, type ReactElement } from "react";
import type { TableGetterSpec } from "@archlang/engine";
import { Button, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { useTranslation } from "../i18n";
import type { GetterOption } from "./useTableColumnEditing.js";

export interface TableColumnPickerProps {
  readonly options: readonly GetterOption[];
  readonly onPick: (getter: TableGetterSpec) => void;
  readonly onClose: () => void;
}

/** Parse a free-text getter (`@repo.url`, `@@team`) into a spec. Returns null
 *  when it doesn't start with a sigil or carries no path. */
function parseGetter(raw: string): TableGetterSpec | null {
  const text = raw.trim();
  const sigil = text.startsWith("@@") ? "@@" : text.startsWith("@") ? "@" : null;
  if (!sigil) return null;
  const rest = text.slice(sigil.length);
  const paren = rest.indexOf("(");
  const pathText = paren >= 0 ? rest.slice(0, paren) : rest;
  const path = pathText.split(".").map((s) => s.trim()).filter(Boolean);
  if (path.length === 0) return null;
  const capabilityArgs =
    paren >= 0 && rest.endsWith(")") ? rest.slice(paren + 1, -1) : undefined;
  return { sigil, path, ...(capabilityArgs !== undefined ? { capabilityArgs } : {}) };
}

export function TableColumnPicker({ options, onPick, onClose }: TableColumnPickerProps): ReactElement {
  const { t } = useTranslation("boardEdit");
  const menu = useContextMenu();
  const [free, setFree] = useState("");
  const parsed = parseGetter(free);

  const itemsFor = (opt: GetterOption): ContextMenuItem[] => [
    {
      label: t("colMenuAdd"),
      onClick: () => {
        onPick(opt.getter);
        onClose();
      },
    },
    {
      label: t("colMenuCopyName"),
      onClick: () => void navigator.clipboard?.writeText(opt.text),
    },
  ];
  return (
    <div
      role="dialog"
      aria-label={t("addColumnTitle")}
      className="absolute z-overlay mt-1 max-h-64 w-56 overflow-auto rounded border border-slate-700 bg-slate-900 py-1 text-xs shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <form
        className="flex gap-1 px-2 py-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (parsed) {
            onPick(parsed);
            onClose();
          }
        }}
      >
        <input
          autoFocus
          aria-label={t("getterAria")}
          placeholder={t("getterPlaceholder")}
          value={free}
          onChange={(e) => setFree(e.target.value)}
          className="min-w-0 flex-1 rounded border border-slate-700 bg-slate-950 px-1.5 py-0.5 text-slate-100 outline-none focus:border-arch-500"
        />
        <Button type="submit" variant="primary" size="xs" disabled={!parsed}>
          {t("add")}
        </Button>
      </form>
      {options.length > 0 && <div className="my-1 border-t border-slate-800" />}
      {options.map((opt) => (
        <button
          key={opt.text}
          type="button"
          className="flex w-full items-center justify-between px-2 py-1 text-left text-slate-300 hover:bg-slate-800 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          onClick={() => {
            onPick(opt.getter);
            onClose();
          }}
          onContextMenu={(e) => menu.open(e, itemsFor(opt))}
        >
          <span className="truncate">{opt.label}</span>
          <span className="ml-2 shrink-0 font-mono text-slate-500">{opt.text}</span>
        </button>
      ))}
      {menu.node}
    </div>
  );
}
